import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import type { Block, MeResponse, PersonBlockRole } from "../../api/types";
import { useRouter } from "../../router/Router";
import { BlockCard } from "./BlockCard";
import { type BlockSortKey, sortBlocks } from "./blockSort";
import { BlockSortSelect } from "./BlockSortSelect";
import { fetchAllBlocks } from "./fetchAllBlocks";
import { SeasonPicker } from "./SeasonPicker";
import { useBlockSeason } from "./useBlockSeason";

interface State {
  status: "loading" | "ready" | "error";
  blocks: Block[];
  errorMessage: string | null;
}

const initial: State = { status: "loading", blocks: [], errorMessage: null };

// One section per role the signed-in user can hold on a block, in a fixed order.
// A block appears under every section whose role its my_roles carries, so a block
// the user both owns and is assigned shows in both (clearer than deduping into
// one, and cheap since my_roles carries every matching role).
const sections: { role: PersonBlockRole; heading: string }[] = [
  { role: "assignee", heading: "Assigned to me" },
  { role: "exec_owner", heading: "Exec-owned by me" },
  { role: "contributor", heading: "Contributing to" }
];

// My Blocks lists every block the signed-in user is involved in this season — as
// assignee, exec owner, or contributor — grouped by role. The season defaults to
// the active one; leadership can switch it via the SeasonPicker (hidden for
// everyone else), matching All Blocks.
export function MyBlocksPage({ me }: { me: MeResponse }) {
  const router = useRouter();
  const [state, setState] = useState<State>(initial);
  const { seasonId, seasons, setSeasonId, ready } = useBlockSeason(me);
  // My Blocks is a personal to-do view, so what's due next leads by default.
  const [sort, setSort] = useState<BlockSortKey>("due");
  // Rapid season switches overlap, and only the latest may land: an older
  // season's blocks would otherwise replace the selected one's.
  const latestRefresh = useRef(0);

  useEffect(() => {
    if (ready) {
      void refresh(seasonId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, ready]);

  async function refresh(season: string) {
    const request = ++latestRefresh.current;
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));
    try {
      const blocks = await fetchAllBlocks(season, { mine: true });
      if (request === latestRefresh.current) {
        setState({ status: "ready", blocks, errorMessage: null });
      }
    } catch (caught) {
      if (request === latestRefresh.current) {
        setState({ status: "error", blocks: [], errorMessage: messageFor(caught) });
      }
    }
  }

  return (
    <div className="blocks-view">
      <section className="card">
        <h2>My Blocks</h2>
        <div className="form-row form-row--inline">
          <SeasonPicker seasons={seasons} value={seasonId} onChange={setSeasonId} />
          <BlockSortSelect value={sort} onChange={setSort} />
        </div>

        {state.status === "loading" && <p className="loading">Loading…</p>}
        {state.status === "error" && state.errorMessage !== null && (
          <p className="form-row__error">{state.errorMessage}</p>
        )}
        {state.status === "ready" && state.blocks.length === 0 && (
          <p className="muted">{emptyMessage(seasonId)}</p>
        )}
        {state.status === "ready" &&
          state.blocks.length > 0 &&
          sections.map(({ role, heading }) => {
            // A mine block always carries ≥1 role, so an empty section here just
            // means the user holds no block in that role — omit it.
            const inRole = sortBlocks(
              state.blocks.filter((block) => (block.my_roles ?? []).includes(role)),
              sort
            );
            if (inRole.length === 0) {
              return null;
            }
            return (
              <div className="my-blocks-section" key={role}>
                <h3>{heading}</h3>
                <div className="block-card-grid">
                  {inRole.map((block) => (
                    <BlockCard key={block.id} block={block} onOpen={(id) => router.navigate(`/blocks/${id}`)} />
                  ))}
                </div>
              </div>
            );
          })}
      </section>
    </div>
  );
}

// emptyMessage distinguishes a deliberately-selected empty season (leadership)
// from the default view when no season is active at all — the user simply relates
// to nothing in either case.
function emptyMessage(seasonId: string): string {
  if (seasonId !== "") {
    return "You're not involved in any blocks this season.";
  }
  return "You're not involved in any blocks yet.";
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
